import { QueryClient } from '@tanstack/react-query';
import { getLocale } from 'next-intl/server';

import { useCompanyAdminListingPageContentQuery } from '@module/mdrt-org/shared/utils/data/graphql/_generated/gql-generated';
import {
  extractKeyValueBlocks,
  extractKeyValueOptions,
  type KeyTranslatedValueContentArea,
} from '@module/mdrt-org/shared/utils/data/graphql/utils/extract-key-value-options';
import { MembershipLevel } from '@module/mdrt-org/shared/utils/enums/membership';
import { MembershipType } from '@module/mdrt-org/shared/utils/types/membership-application';
import { mapIntlLocaleToOptimizelyLocale } from '@shared/utils/helpers/map-intl-locale-to-optimizely-locale';

import { CompanyListingType } from './utils/get-company-listings/types';

type DescriptionButtonExpanded = {
  Description?: Nullable<string>;
  Heading?: Nullable<string>;
};
type ContentArea = Array<
  Nullable<{
    ContentLink?: Nullable<{
      Expanded?: Nullable<DescriptionButtonExpanded | Record<string, unknown>>;
    }>;
  }>
>;

const extractBottomContent = (items: Nullable<ContentArea> | undefined) => {
  if (!items) {
    return undefined;
  }

  for (const item of items) {
    const expanded = item?.ContentLink?.Expanded as DescriptionButtonExpanded | null | undefined;

    if (expanded?.Heading || expanded?.Description) {
      return {
        description: expanded?.Description ?? '',
        heading: expanded?.Heading ?? '',
      };
    }
  }

  return undefined;
};

export const MEMBERSHIP_LEVEL_ALL = 'ALL';

export type MembershipLevelSelection = MembershipLevel | typeof MEMBERSHIP_LEVEL_ALL;

const COMPANY_LISTING_TYPE_VALUES = new Set<string>(Object.values(CompanyListingType));
const MEMBERSHIP_LEVEL_VALUES = new Set<string>([
  ...Object.values(MembershipLevel),
  MEMBERSHIP_LEVEL_ALL,
]);

const extractListTypeOptions = (items: Nullable<KeyTranslatedValueContentArea> | undefined) =>
  extractKeyValueOptions<CompanyListingType>(items, COMPANY_LISTING_TYPE_VALUES);

const extractMembershipLevelOptions = (
  items: Nullable<KeyTranslatedValueContentArea> | undefined
) => extractKeyValueOptions<MembershipLevelSelection>(items, MEMBERSHIP_LEVEL_VALUES);

const STATUS_ID_REMAINING = 'REMAINING';

const extractStatusIdToProblemMap = (
  items: Nullable<KeyTranslatedValueContentArea> | undefined
): { byId: Record<number, string>; fallback: null | string } => {
  const byId: Record<number, string> = {};
  let fallback: null | string = null;

  for (const block of extractKeyValueBlocks(items)) {
    if (!block.Key) continue;
    if (block.Key.trim() === STATUS_ID_REMAINING) {
      fallback = block.Value ?? '';
      continue;
    }

    for (const part of block.Key.split(',')) {
      const id = Number(part.trim());
      if (Number.isFinite(id)) {
        byId[id] = block.Value ?? '';
      }
    }
  }

  return { byId, fallback };
};

// eslint-disable-next-line complexity
export const getCompanyListingPageContent = async () => {
  const queryClient = new QueryClient();
  const locale = mapIntlLocaleToOptimizelyLocale(await getLocale());
  const siteId = process.env.OPTIMIZELY_SITE_ID ?? '';

  const variables = { locale, siteId };

  const data = await queryClient.fetchQuery({
    queryFn: useCompanyAdminListingPageContentQuery.fetcher(variables),
    queryKey: useCompanyAdminListingPageContentQuery.getKey(variables),
  });

  const item = data.CompanyAdminCompanyListingsPage?.items?.[0];
  if (!item) return undefined;

  const siteSettings = data.NextSiteSettingsPage?.items?.[0];
  const membershipLevelLabels = new Map<string, string>([
    [MembershipLevel.COT, siteSettings?.COTLevelLabel || 'Court of the Table'],
    [MembershipLevel.MDRT, siteSettings?.MDRTLevelLabel || 'MDRT'],
    [MembershipLevel.TOT, siteSettings?.TOTLevelLabel || 'Top of the Table'],
  ]);
  const membershipTypeLabels = new Map<string, string>([
    [MembershipType.LifeMember, siteSettings?.LifeMembershipTypeLabel || 'Life Member'],
    [
      MembershipType.QualifyingLifeMember,
      siteSettings?.QualifyingLifeMembershipTypeLabel || 'Qualifying and Life Member',
    ],
    [
      MembershipType.QualifyingMember,
      siteSettings?.QualifyingMembershipTypeLabel || 'Qualifying Member',
    ],
  ]);

  return {
    additionalSearchCriteriaDropDownLabel:
      item?.AdditionalSearchCriteriaDropDownLabel || 'Available Lists',
    additionalSearchCriteriaDropDownRequiredMessage:
      item?.AdditionalSearchCriteriaDropDownRequiredMessage || 'Required',
    affiliatedCompanyIdLabel: item?.AffiliatedCompanyIdLabel || 'Company Information',
    backToDashboardLink: item?.BackToDashboardLink,
    bottomContent: extractBottomContent(item?.BottomContent),
    companyLabel: item?.CompanyLabel || 'Company: ',
    downloadCompanyListingsLink: item?.DownloadCompanyListingsLink,
    errorResultsText: item?.ErrorResultsText || 'Error while loading data.',
    listTypeOptions: extractListTypeOptions(item.AdditionalSearchCriteriaDropDown),
    membershipLevelDropDownLabel: item?.MembershipLevelDropDownLabel || 'Membership Level',
    membershipLevelLabels,
    membershipLevelOptions: extractMembershipLevelOptions(item.MembershipLevelDropDown),
    membershipTypeLabels,
    membersTableHeaderLabels: {
      addressLine: item?.MembersTableHeaderLabels?.AddressLine || 'Address Line',
      agentId: item?.MembersTableHeaderLabels?.AgentID || 'Agent ID',
      approvedDate: item?.MembersTableHeaderLabels?.ApprovedDate || 'Approved Date',
      attendeeStatus: item?.MembersTableHeaderLabels?.AttendeeStatus || 'Attendee Status',
      city: item?.MembersTableHeaderLabels?.City || 'City',
      country: item?.MembersTableHeaderLabels?.Country || 'Country',
      firstName: item?.MembersTableHeaderLabels?.FirstName || 'First name',
      lastName: item?.MembersTableHeaderLabels?.LastName || 'Last name',
      mdrtId: item?.MembersTableHeaderLabels?.MDRTID || 'MDRT ID',
      membershipLevel: item?.MembersTableHeaderLabels?.MembershipLevel || 'Membership Level',
      memberStatus: item?.MembersTableHeaderLabels?.MemberStatus || 'Member Status',
      phoneNumber: item?.MembersTableHeaderLabels?.MobileCellPhoneNumber || 'Phone Number',
      problem: item?.MembersTableHeaderLabels?.Problem || 'Problem',
      state: item?.MembersTableHeaderLabels?.State || 'State',
      totalYearsOfMembership:
        item?.MembersTableHeaderLabels?.TotalYearsOfMembership || 'Years of Membership',
    },
    mentorAspirantDropdownValue: item?.MentorAspirantDropdownValue,
    noResultsText: item?.NoResultsText || 'No results found',
    numbersOfPageLabel: item?.NumbersofPageLabel || '{{pageCount}} Pages',
    pageDescription: item?.PageDescription || '',
    pageTitle: item?.PageTitle || 'Company Listing',
    // CMS field is rich-text, so content arrives wrapped in <p>; stripping tags is necessary for MessageFormat
    recordsFoundText: (item?.RecordsFoundText || '{totalResults} Members').replaceAll(
      /<[^>]+>/gu,
      ''
    ),
    rowsPerPageLabel: item?.RowsPerPageLabel || 'Rows per page: {{recordsPerPage}}',
    searchButtonText: item?.SearchButtonText || 'Search',
    searchSectionTitle: item?.SearchSectionTitle || 'Report Selection',
    statusIdToProblemMap: extractStatusIdToProblemMap(item.MembershipStatusIdToProblemMap),
    yearDropDownLabel: item?.YearDropDownLabel || 'Year',
    yearDropDownRequiredMessage: item?.YearDropDownRequiredMessage || '',
  };
};

export type CompanyListingPageContent = NonNullable<
  Awaited<ReturnType<typeof getCompanyListingPageContent>>
>;
